import { useCallback, useEffect, useRef, useState } from "react"
import { HashRouter } from "react-router"
import { useAppDispatch, useAppSelector } from "../hooks/reduxToolkit"
import { SplashScreen } from "../screens/splashScreen"
import { persistor, store } from "../reduxToolkit/store"
import { PersistGate } from "redux-persist/integration/react"
import { legacySessionForNativeMigration, useClient, useIsOnline, useSession } from "../providers/supabase"
import Onboarding from "../screens/onboarding"
import { SignIn } from "../screens/signIn"
import { OnlineRequired } from "../screens/onlineRequired"
import { MfaChallenge } from "../screens/mfaChallenge"
import { loadReduxStateFromServer, membershipSyncPayload } from "../../lib/queries/loadReduxStateFromServer"
import { onSyncMembership } from "../reduxToolkit/membershipSlice"
import { onSyncDependents } from "../reduxToolkit/dependentsSlice"
import { useSentry } from "../providers/sentry"
import { awaitNativeSession } from "../../lib/nativeAuth"
import { expoSupportsAuth } from "../../lib/nativeBridge"

type StartupFlowState =
  'AwaitNativeSession' |
  'LoadStateFromStorage' |
  'ChallengeMfa' |
  'ReloadStateFromServer' |
  'SignInForm' |
  'ShowOnlineRequiredPage' |
  'ShowOnboarding' |
  'Complete'

interface StartupFlowInitializerProps {
  onComplete: () => void
}

// How long AwaitDelayedNativeSession waits for native to finish adopting a
// handed-over legacy session before offering the user a reload. Generous
// relative to the 10s handshake timeout in nativeAuth.ts, because the work
// being waited on here (native's own setSession, which can hit the network)
// legitimately takes longer than answering a request-auth-session.
const DELAYED_NATIVE_SESSION_TIMEOUT_MS = 20000

/**
 * This initializer handles the Startup sequence for the app.
 * See the Startup Flow Chart in the docs for more details.
 */
export function StartupFlowInitializer({
    children,
    onComplete
}: React.PropsWithChildren<StartupFlowInitializerProps>) {
  const isOnline = useIsOnline()
  const session = useSession()
  const client = useClient()
  const Sentry = useSentry()
  const localOnboardingCompletedAt = useAppSelector(state => state.membership?.onboardingCompletedAt || 0)

  const onlineUserId = session?.user?.id

  // Native builds that support the Phase-2 handoff must resolve their
  // session via native before anything else — including before
  // LoadStateFromStorage, which would otherwise read a stale/absent
  // membership.userId off of a session context that hasn't been populated
  // by native yet. Browsers and native builds that predate the handoff
  // (window.expoSupportsAuth unset) skip straight to today's flow.
  const [flowState, _setFlowState] = useState<StartupFlowState>(
    expoSupportsAuth() ? 'AwaitNativeSession' : 'LoadStateFromStorage'
  )

  const setFlowState = useCallback((newState: StartupFlowState) => {
    Sentry.addBreadcrumb({
      category: 'startup',
      message: `Setting flow state to ${newState}`,
      level: 'info',
      data: {
        previousFlowState: flowState,
        newFlowState: newState,
        onlineUserId,
        isOnline,
      }
    })
    _setFlowState(newState)
  }, [Sentry, flowState, _setFlowState, onlineUserId, isOnline])

  const [error, setError] = useState<Error>()

  console.log('StartupFlowInitializer', {isOnline, userId: session?.user?.id}, {flowState, isOnline, localOnboardingCompletedAt})

  if (error) { throw error }

  switch(flowState) {
    case 'AwaitNativeSession':
      return <AwaitNativeSession
        onSettled={() => setFlowState('LoadStateFromStorage')} />

    case 'LoadStateFromStorage':
      return <PersistGate
        loading={<SplashScreen />}
        persistor={persistor}
        onBeforeLift={() => {
          const doIt = async () => {
            // This callback is executed before the redux hooks get updated, so we can't use the hooks here.
            const state = store.getState()
            console.log('onBeforeLift', {isOnline, userId: onlineUserId }, 'state.membership.userId', state.membership?.userId, 'state.membership.onboardingCompletedAt', state.membership?.onboardingCompletedAt)
            if (isOnline) {
              // If we're online, check the online user ID
              if (onlineUserId) {
                if (state.membership?.userId && state.membership.userId !== onlineUserId) {
                  // We're signed in as a different user, so we need to purge & reload the state
                  await persistor.purge()
                  await persistor.flush()
                }
                setFlowState('ChallengeMfa')
              } else {
                setFlowState('SignInForm')
              }
            } else {
              // If we're not online, check local onboarding state
              const offlineUserId = state.membership?.userId
              if (offlineUserId) {
                if (state.membership.onboardingCompletedAt > 0) {
                  setFlowState('Complete')
                  onComplete()
                } else {
                  setFlowState('ShowOnlineRequiredPage')
                }
              } else {
                setFlowState('ShowOnlineRequiredPage')
              }
            }
          }

          doIt().catch((err) => setError(err))
        }}>
          <SplashScreen />
      </PersistGate>
    case 'ChallengeMfa':
      return <MfaStepUpGate
        onPassed={() => setFlowState('ReloadStateFromServer')}
        onError={(err) => setError(err)} />

    case 'ReloadStateFromServer':
      return <ReloadStateFromServer
        onStateLoaded={({hasCompletedOnboarding}) => {
          if (hasCompletedOnboarding) {
            setFlowState('Complete')
            onComplete()
          } else if (isOnline) {
            setFlowState('ShowOnboarding')
          } else {
            setFlowState('ShowOnlineRequiredPage')
          }
        }}
        onError={(err) => {
          if (/NetworkError/.test(err.message)) {
            // If a network error occurs, check the local state for the "Onboarding Complete"

            if (localOnboardingCompletedAt > 0) {
              setFlowState('Complete')
              onComplete()
            } else {
              setFlowState('ShowOnlineRequiredPage')
            }
          } else {
            setError(err)
          }
        }} />

    case 'SignInForm':
      if (expoSupportsAuth()) {
        // Hard backstop: a native build that supports the Phase-2 handoff
        // must never reach web sign-in, even if some other state transition
        // ends up setting flowState to 'SignInForm'. The common real case:
        // AwaitNativeSession asked native for a session, got none, and (for
        // the existing-install migration) handed native a legacy session to
        // adopt — but native adopting it and pushing it back is async, so by
        // the time it arrives, LoadStateFromStorage's onlineUserId check has
        // already fallen through here. AwaitDelayedNativeSession watches for
        // that session arriving and rejoins the flow instead of leaving the
        // user on a splash screen forever.
        return <AwaitDelayedNativeSession onSessionArrived={() => setFlowState('LoadStateFromStorage')} />
      }
      return <SignIn onLoginSuccess={(data, navigateTo) => {
        const doIt = async () => {
          const state = store.getState()
          if (state?.membership?.userId && data.session.user && data.session.user.id !== state.membership.userId) {
            // We signed in as a different user, so we need to clear our stored state
            await persistor.purge()
            await persistor.flush()
          }

          // Clear out any hash params
          const url = new URL(window.location.href)
          url.pathname = navigateTo || ''
          url.hash = ''
          url.search = ''
          window.history.pushState({}, '', url)
          setFlowState('ChallengeMfa')
        }

        doIt()
          .catch((err) => setError(err))
      }} />

    case 'ShowOnlineRequiredPage':
      return <OnlineRequired onConnectionEstablished={() => {
        // double-check our session
        client.auth.getSession()
          .then(({data, error}) => {
            if (error) {
              throw new Error('Failed to get session', {
                cause: error,
              });
            }
            if (data?.session?.user?.id) {
              setFlowState('ChallengeMfa')
            } else {
              setFlowState('SignInForm')
            }
          })
          .catch((err) => setError(err))
      }} />

    case 'ShowOnboarding':
      return <HashRouter>
        <Onboarding onComplete={() => {
          setFlowState('Complete')
          // Clear out any hash params
          const url = new URL(window.location.href)
          url.pathname = ''
          url.hash = ''
          url.search = ''
          window.history.pushState({}, '', url)
          onComplete()
        }} />
      </HashRouter>

    case 'Complete':
      return <>{children}</>

    default:
      raiseUnknownState(flowState)
  }
}

/**
 * Implements the view for the ReloadStateFromServer state.
 * This state is only entered if the user is signed in and online.
 */
function ReloadStateFromServer({
  onStateLoaded,
  onError
} : {
  onStateLoaded: (state: { hasCompletedOnboarding: boolean }) => void
  onError: (error: Error) => void
}) {
  const dispatch = useAppDispatch()
  const user = useSession()?.user
  const client = useClient()

  if (!user) { throw new Error('User is not signed in') }

  useEffect(() => {
    loadReduxStateFromServer(client)
      .then(
        (result) => {
          if (result.membership) {
            // Built by membershipSyncPayload rather than spelled out here, so a field
            // added to the loader cannot be silently left out of the dispatch.
            dispatch(onSyncMembership(membershipSyncPayload(result, user.id)))
          }
          if (result.dependents) {
            dispatch(onSyncDependents({
              dependents: result.dependents,
              profiles: result.dependentProfiles
            }))
          }
          onStateLoaded(result)
        },
        (error) => onError(error)
      )
  }, [dispatch, user, client, onStateLoaded, onError])

  return <SplashScreen />
}


function raiseUnknownState(state: never): never {
  throw new Error(`Unknown flow state: ${state}`)
}

/**
 * Phase 2 of the expo-owned sign-in handoff (docs/plans/expo-owned-signin.md).
 * The first startup state for native builds that support the handoff: ask
 * native for the current session (applying it if present, via the
 * always-on listener registered in supabase.tsx), or hand native our
 * pre-Phase-2 legacy session if native has none of its own (existing-install
 * migration). Renders a splash while waiting.
 *
 * On timeout, shows a distinct retry state rather than falling through —
 * `SignInForm` is separately hard-gated behind `!expoSupportsAuth()` so this
 * component is the only thing standing between a native build and getting
 * stuck, and it must not silently give up.
 *
 * The same retry state also covers a session native hands over that
 * `setSession` then rejects (e.g. already-revoked): as of Phase 3 the
 * handshake settles only once the session has actually been applied, so that
 * failure rejects here instead of being logged out of sight.
 */
function AwaitNativeSession({
  onSettled,
}: {
  onSettled: () => void
}) {
  const Sentry = useSentry()
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  // StartupFlowInitializer's setFlowState (and so this inline onSettled prop)
  // gets a new identity on every parent re-render (isOnline toggling, etc.),
  // not just when the caller actually wants a new attempt. A ref keeps the
  // effect below from re-running — and re-issuing the handshake — on every
  // unrelated parent render; only `attempt` (bumped by the retry button)
  // should trigger a fresh attempt.
  const onSettledRef = useRef(onSettled)
  useEffect(() => { onSettledRef.current = onSettled })

  useEffect(() => {
    let cancelled = false
    setFailed(false)
    awaitNativeSession(legacySessionForNativeMigration).then(
      () => {
        if (cancelled) { return }
        onSettledRef.current()
      },
      (err) => {
        if (cancelled) { return }
        // Either native never answered, or it answered with a session the
        // client refused (revoked/expired pair) — both land here as of
        // Phase 3, and both are retryable from the user's point of view.
        Sentry.captureMessage('Native auth handshake failed', {
          level: 'warning',
          extra: { error: err instanceof Error ? err.message : String(err), attempt },
        })
        setFailed(true)
      }
    )
    return () => { cancelled = true }
  }, [Sentry, attempt])

  if (failed) {
    return <div className="App container sign-in">
      <div className="row mt-4 justify-content-center">
        <div className="col-12 col-lg-6 text-center">
          <h1>Couldn&rsquo;t reach the app</h1>
          <p>We weren&rsquo;t able to verify your sign-in. Please try again.</p>
          <button type="button" className="btn btn-primary" onClick={() => setAttempt((a) => a + 1)}>
            Try again
          </button>
        </div>
      </div>
    </div>
  }

  return <SplashScreen />
}

/**
 * The `SignInForm` case's hard-gate fallback for native builds (see its
 * comment). Native adopting a hand-over legacy session and pushing it back
 * (the existing-install migration path) is asynchronous relative to
 * `AwaitNativeSession` already having moved on to `LoadStateFromStorage` →
 * `SignInForm` with no session yet — so this watches `useSession()` and
 * rejoins the flow the moment one arrives, instead of sitting on a splash
 * screen forever waiting for a state transition nothing would otherwise
 * trigger.
 */
function AwaitDelayedNativeSession({ onSessionArrived }: { onSessionArrived: () => void }) {
  const session = useSession()
  const [gaveUp, setGaveUp] = useState(false)

  const onSessionArrivedRef = useRef(onSessionArrived)
  useEffect(() => { onSessionArrivedRef.current = onSessionArrived })

  useEffect(() => {
    if (session) {
      onSessionArrivedRef.current()
    }
  }, [session])

  // The fast-failure path resolves on its own — App.tsx's
  // finishMigrationCheck still runs, hasAttemptedLegacyMigration flips true,
  // and native unmounts this whole webview in favor of NativeSignIn. What
  // that doesn't cover is native's setSession *hanging* rather than failing
  // (a stuck SecureStore or network call), which would leave
  // finishMigrationCheck unrun and this component on an indefinite splash.
  // Bound the wait so the user gets an action instead of a frozen screen.
  useEffect(() => {
    if (session) { return }
    const timer = setTimeout(() => setGaveUp(true), DELAYED_NATIVE_SESSION_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [session])

  if (gaveUp) {
    return <div className="App container sign-in">
      <div className="row mt-4 justify-content-center">
        <div className="col-12 col-lg-6 text-center">
          <h1>This is taking longer than expected</h1>
          <p>We couldn&rsquo;t finish signing you in. Please try again.</p>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    </div>
  }

  return <SplashScreen />
}

/**
 * Gate that checks the session's authenticator assurance level. If the user
 * has a verified TOTP factor and the current session is still aal1, render
 * the MFA challenge UI to step up to aal2 before continuing.
 *
 * Exported so cucumber specs can drive the real component instead of
 * re-implementing the decision logic.
 */
export function MfaStepUpGate({
  onPassed,
  onError,
}: {
  onPassed: () => void
  onError: (err: Error) => void
}) {
  const client = useClient()
  const Sentry = useSentry()
  const [needsChallenge, setNeedsChallenge] = useState<boolean | null>(null)
  const [assuranceError, setAssuranceError] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setAssuranceError(false)
    setNeedsChallenge(null)
    client.auth.mfa.getAuthenticatorAssuranceLevel().then(
      ({ data, error }) => {
        if (cancelled) { return }
        if (error) {
          // Fail closed. An assurance-level error is not evidence that this
          // session is allowed through. Stay on the gate and report it.
          Sentry.captureMessage('MFA AAL check failed — staying on the sign-in gate', {
            level: 'error',
            extra: { error: error.message, name: error.name },
          })
          setAssuranceError(true)
          return
        }
        if (data?.currentLevel === 'aal1' && data?.nextLevel === 'aal2') {
          setNeedsChallenge(true)
        } else {
          onPassed()
        }
      },
      (err) => {
        if (cancelled) { return }
        Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
          tags: { feature: 'mfa', mfa_op: 'assurance_level' },
        })
        setAssuranceError(true)
      }
    )
    return () => { cancelled = true }
  }, [client, onPassed, Sentry, attempt])

  // Kept for callers. An assurance failure stays on this gate instead of
  // throwing into the startup error screen, so the member can retry.
  void onError

  if (assuranceError) {
    return <div className="App container sign-in">
      <div className="row mt-4 justify-content-center">
        <div className="col-12 col-lg-6 text-center">
          <h1>Two-factor verification</h1>
          <p>We couldn&rsquo;t confirm your sign-in security. You stay here until that check succeeds.</p>
          <button type="button" className="btn btn-primary" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    </div>
  }

  if (needsChallenge) {
    return <MfaChallenge onVerified={onPassed} />
  }
  return <SplashScreen />
}
